import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import MaskedTextField from "@/common/components/MaskedTextField"
import SelectController from "@/common/components/SelectController"
import Spinner from "@/common/components/Spinner"
import Tabs from "@/common/components/Tabs"
import TextField from "@/common/components/TextField"
import { nameMask, phoneMask } from "@/common/constants/masks.constant"
import { useEstablishmentUser } from "@/common/hooks/queries/useEstablishmentUser"
import { useUpdateEstablishmentUser } from "@/features/establishments/hooks/useUpdateEstablishmentUser"
import { establishmentUserSchema, EstablishmentUserValidationSchema } from "@/features/establishments/schemas/create.schema"
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany"
import { statusOptions } from "@/features/users/constants/statusOptions.constant"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { ChevronsRightIcon } from "lucide-react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { useEstablishmentRoles } from "@/common/hooks/queries/useEstablishmentRoles";
import { Mapper } from "@/common/services/mapper";
import { RoleData } from "@/common/types/roles";
import useDebounce from "@/common/hooks/useDebounce";
import { formatCpf } from "@/common/utils/formatters"
import { dateTransformer } from "@/common/utils/dateTransformer"
import GoBackButton from "@/common/components/GoBackButton";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useState } from "react"
import { FormTransformers } from "@/common/utils/formTransformers"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import { ImageUploader } from "@/common/components/ImageUploader"
import { checkIsImage } from "@/common/utils/fileValidator"

const Update = () => {
  const { establishmentId, id } = useParams()
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [file, setFile] = useState<Blob>();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const { data: establishment } = useEstablishmentCompany(establishmentId || '')

  const [searchRole, setSearchRole] = useDebounce('');

  const { data: rolesOptions } = useEstablishmentRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  const {
    data: establishmentUser,
    isPending: establishmentUserIsLoading,
  } = useEstablishmentUser({ id: id || '' })

  const {
    mutate: updateClientUser,
    isPending: updateClientUserIsPending,
  } = useUpdateEstablishmentUser({
    establishmentUser,
    contactId: id,
    establishmentId,
    file,
    onSuccess: () => {
      setIsError(false);
      queryClient.invalidateQueries({ queryKey: ['establishmentUser', id] })
      goBack({ fallback: `/establishments/${establishmentId}/users/${id}` })
    },
    onError: (error) => {
      setIsError(true);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);

      FormTransformers.errorsTransformer({ setError })(error);
    }
  })

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors }
  } = useForm<EstablishmentUserValidationSchema>({
    resolver: zodResolver(establishmentUserSchema),
    defaultValues: {
      cpf: establishmentUser?.cpf ? formatCpf(establishmentUser.cpf) : undefined,
      date_of_birth: establishmentUser?.date_of_birth ? dateTransformer(establishmentUser?.date_of_birth) : undefined,
      full_name: establishmentUser?.full_name,
      status: statusOptions.find(({ value }) => value === establishmentUser?.status),
      phone: {
        number: establishmentUser?.phone?.number ? formatCpf(establishmentUser.phone.number) : undefined,
      },
      email: establishmentUser?.email || undefined,
      roles: establishmentUser?.user_roles?.map(({ role }) => ({ value: role.id, label: role.display_name })),
    }
  })

  const onSubmit: SubmitHandler<EstablishmentUserValidationSchema> = (data) => updateClientUser(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <GoBackButton fallback={`/establishments/${establishmentId}/users/${id}`} />
            <p className="text-white-400 font-normal">{establishment?.general_information?.fantasy_name}</p>
            <ChevronsRightIcon color='#ffffffb3' />
            <h2>{establishmentUser?.full_name}</h2>
          </div>
          {!establishmentUserIsLoading && (
            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            >
              {updateClientUserIsPending ? (
                <Spinner className="h-6 mx-[23px] fill-white-800" />
              ) : (
                'Confirmar'
              )}
            </Button>
          )}
        </div>
        <Tabs
          tabsListClassName="w-full bg-transparent rounded-none"
          tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
          tabsContentClassName="mt-6"
          tabsList={[
            {
              label: 'Dados',
              value: '',
              body: (
                <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-192px)]">
                  <p className="text-lg font-bold">Dados pessoais</p>
                  <ImageUploader
                    isLoading={false}
                    label="Foto"
                    name="profile-picture"
                    accept="image/*"
                    onChange={(e) => {
                      const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
                      if (!file || !checkIsImage(file)) {
                        return
                      }
                      setFile((e.target as HTMLInputElement & { files: FileList }).files[0])
                    }}
                    currentImageUrl={file
                      ? URL.createObjectURL(file)
                      : establishmentUser?.profile_picture?.url
                        ? establishmentUser?.profile_picture?.url
                        : undefined
                    }
                    disabled={updateClientUserIsPending}
                    errorMessage={errors.profile_picture?.message as string | undefined}
                  />
                  <div className="flex gap-2 w-full justify-between">
                    <div className="w-3/4">
                      <MaskedTextField
                        label="CPF"
                        mask='000.000.000-00'
                        errorMessage={errors.cpf?.message || undefined}
                        {...register('cpf')}
                        markAsRequired
                        disabled={updateClientUserIsPending}
                      />
                      <Button variant='link'>Importar dados</Button>
                    </div>
                    <MaskedTextField
                      label="Data de nascimento"
                      markAsRequired
                      mask="00/00/0000"
                      className="w-1/4"
                      errorMessage={errors.date_of_birth?.message || undefined}
                      {...register('date_of_birth')}
                      disabled={updateClientUserIsPending}
                    />
                  </div>
                  <div className="flex gap-2 w-full justify-between">
                    <MaskedTextField
                      label="Nome"
                      className="w-3/4"
                      mask={nameMask}
                      maxLength={64}
                      errorMessage={errors.full_name?.message || undefined}
                      {...register('full_name')}
                      markAsRequired
                      disabled={updateClientUserIsPending}
                    />
                    <SelectController
                      control={control}
                      name="roles"
                      label="Perfil"
                      className="w-1/4"
                      options={rolesOptions}
                      onInputChange={setSearchRole}
                      errorMessage={errors.roles?.message || undefined}
                      isMulti
                      markAsRequired
                    />
                  </div>
                  <SelectController
                    control={control}
                    name="status"
                    label="Status"
                    options={statusOptions}
                    errorMessage={errors.status?.message || undefined}
                    markAsRequired
                    isDisabled={updateClientUserIsPending}
                  />
                  <MaskedTextField
                    label="Telefone"
                    mask={phoneMask}
                    errorMessage={errors.phone?.number?.message || undefined}
                    {...register('phone.number')}
                    disabled={updateClientUserIsPending}
                  />
                  <TextField
                    label="Email"
                    {...register('email')}
                    errorMessage={errors.email?.message || undefined}
                    disabled={updateClientUserIsPending}
                  />
                </Box>
              )
            },
            {
              label: 'Histórico de consumo',
              value: 'consumptionHistory',
              disabled: true,
              body: <></>
            },
            {
              label: 'Histórico de ações',
              value: 'actionsHistory',
              disabled: true,
              body: <></>
            },
          ]}
        />
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </>
  )
}

export default Update
